import { QueryResult } from '@apollo/client';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Icon,
  Skeleton,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { MailIcon, PlusIcon } from '@heroicons/react/outline';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import {
  FundraisingType,
  getBrand,
  getHostName,
  isFeatureEnabled,
  routes,
} from '@blockpulse3/data/shared';
import {
  CompanyType,
  Exact,
  FundraisingStatus,
  GetAllFundraisingsCountQuery,
  GetFundraisingsCountQuery,
  InputMaybe,
  useGetAllFundraisingsCountQuery,
  useGetCompanyQuery,
  useGetFundraisingsCountQuery,
  useUpdateCompanyMutation,
} from '@blockpulse3/graphql/hooks';
import { ErrorQueryCard } from '@blockpulse3/ui/commons';

import {
  FundraisingHeadlessCompanyModal,
  IFundraisingHeadlessCompanyDefaultValues,
  IFundraisingHeadlessCompanyModal,
} from './FundraisingHeadlessCompanyModal';
import {
  FundraisingTableCurrent,
  FundraisingTableDrafts,
  FundraisingTablePassed,
} from './FundraisingTable';
import { FundraisingTypeModal } from './FundraisingTypeModal';

export type FundraisingsCountQueryType =
  | QueryResult<
      GetFundraisingsCountQuery,
      Exact<{
        companyId?: InputMaybe<string> | undefined;
      }>
    >
  | QueryResult<
      GetAllFundraisingsCountQuery,
      Exact<{
        [key: string]: never;
      }>
    >;

type Props = {
  fundraisingsCount: FundraisingsCountQueryType;
};

/**
 * FundraisingList.
 * Dashboard section of fundraising tables (DRAFT, PASSED, STARTED).
 *
 * @returns {JSX.Element}
 */
export function FundraisingList(): JSX.Element | null {
  const i18nFundraisingList = useTranslations('FundraisingList');
  const i18nFundraisingTableEmpty = useTranslations('FundraisingTableEmpty');
  const fundraisingTypeModal = useDisclosure();
  const fundraisingHeadlessCompanyModal = useDisclosure();

  const location = useLocation();
  const navigate = useNavigate();
  const { companyId = '' } = useParams();
  const companyReq = useGetCompanyQuery({
    variables: { companyId },
    skip: !companyId,
  });
  const company = companyReq?.data?.company;

  const isManaging: boolean = location.pathname?.includes('/manage');

  const [updateCompanyInformations] = useUpdateCompanyMutation();

  const isFeatureFundraisingHeadless = isFeatureEnabled(
    getBrand(getHostName()),
    'fundraisingHeadless',
  );

  const allFundraisingsCount = useGetAllFundraisingsCountQuery({
    fetchPolicy: 'cache-and-network',
    skip: !isManaging,
  });

  const fundraisingsCount = useGetFundraisingsCountQuery({
    variables: { companyId: companyId || '' },
    fetchPolicy: 'cache-and-network',
    skip: !companyId,
  });

  if (fundraisingsCount.loading || allFundraisingsCount.loading) {
    return (
      <Skeleton>
        <Card h="360px" />
      </Skeleton>
    );
  }

  if (fundraisingsCount.error || allFundraisingsCount.error) {
    return <ErrorQueryCard h="360px" />;
  }

  const canUseFeature =
    !company?.type ||
    !(
      (!isFeatureFundraisingHeadless && company?.type === CompanyType.SPV) ||
      (isFeatureFundraisingHeadless &&
        [CompanyType.SPV, CompanyType.PARTIAL, CompanyType.TOKENIZED].includes(company?.type))
    );

  if (!isManaging && canUseFeature) {
    return null;
  }

  const {
    started = 0,
    passed = 0,
    drafts = 0,
  } = {
    ...(fundraisingsCount.data?.getFundraisingsCount || {}),
    ...(allFundraisingsCount.data?.getAllFundraisingsCount || {}),
  };

  const handleNewFundraisingClick = (): void => {
    if (!companyId) return;

    if (isFeatureEnabled(getBrand(getHostName()), 'fundraisingCrowdfunding')) {
      if (company?.type === CompanyType.PARTIAL && isFeatureFundraisingHeadless) {
        return fundraisingHeadlessCompanyModal.onOpen();
      }

      return fundraisingTypeModal.onOpen();
    }
    navigate(routes.workspace.fundraising.private.create.fmt(companyId));
  };

  const handleNewFundraisingTypeClick = (fundraisingType: FundraisingType): void => {
    if (!companyId) return;

    if (fundraisingType === FundraisingType.PRIVATE) {
      navigate(routes.workspace.fundraising.private.create.fmt(companyId));
    } else {
      navigate(routes.workspace.fundraising.crowdfunding.create.fmt(companyId));
    }
  };

  const handleFundraisingDraftClick = (
    fundraisingId: string,
    fundraisingType: FundraisingType,
    fundraisingStatus: FundraisingStatus,
    fundraisingCompanyId?: string,
  ): void => {
    if (!companyId) {
      return;
    }
    if (isManaging || fundraisingStatus !== FundraisingStatus.DRAFT) {
      return handleFundraisingClick(fundraisingId, fundraisingType, fundraisingCompanyId);
    }
    if (fundraisingType === FundraisingType.PRIVATE) {
      navigate(routes.workspace.fundraising.private.parameters.fmt(companyId, fundraisingId));
    } else {
      navigate(routes.workspace.fundraising.crowdfunding.parameters.fmt(companyId, fundraisingId));
    }
  };

  const handleFundraisingClick = (
    fundraisingId: string,
    fundraisingType: FundraisingType,
    fundraisingCompanyId?: string,
  ): void => {
    if (isManaging && fundraisingCompanyId) {
      navigate(routes.admin.company.fundraising.fmt(fundraisingCompanyId, fundraisingId));
    }

    if (companyId) {
      navigate(routes.workspace.fundraising.fmt(companyId, fundraisingId));
    }

    return;
  };

  const handleSubmit = (data: IFundraisingHeadlessCompanyModal): void => {
    if (companyId) {
      updateCompanyInformations({
        variables: {
          updateCompanyInput: {
            companyId,
            nominalValue: data.nominalValue,
            capitalType: data.capitalType,
            minimumShareCapital: data.minimumShareCapital || null,
            maximumShareCapital: data.maximumShareCapital || null,
            shareCapital: data.shareCapital,
          },
        },
        onCompleted: () => {
          handleNewFundraisingTypeClick(data.fundraisingType);
        },
      });
    }
  };

  const hasFundraising = started + passed + drafts > 0;

  const defaultValuesFundraisingHeadlessCompanyModal: IFundraisingHeadlessCompanyDefaultValues = {
    nominalValue: company?.nominalValue || 0,
    shareCapital: company?.shareCapital || 0,
    minimumShareCapital: null,
    maximumShareCapital: null,
  };

  return (
    <Card>
      <FundraisingTypeModal
        isOpen={fundraisingTypeModal.isOpen}
        onClick={handleNewFundraisingTypeClick}
        onClose={fundraisingTypeModal.onClose}
      />
      <FundraisingHeadlessCompanyModal
        companyName={company?.name || ''}
        defaultValues={defaultValuesFundraisingHeadlessCompanyModal}
        isFeatureFundraisingHeadless={isFeatureFundraisingHeadless}
        isOpen={fundraisingHeadlessCompanyModal.isOpen}
        onClose={fundraisingHeadlessCompanyModal.onClose}
        onSubmit={handleSubmit}
      />
      {!isManaging && (
        <CardHeader>
          <Stack
            alignItems={{ base: 'flex-start', md: 'center' }}
            direction={{ base: 'column', md: 'row' }}
            justifyContent="space-between"
          >
            <Heading size="md">{i18nFundraisingList('MyFundraising')}</Heading>

            <Button
              data-cy="new-fundraising"
              isDisabled={!company}
              leftIcon={<Icon as={PlusIcon} boxSize="18px" />}
              onClick={handleNewFundraisingClick}
            >
              {i18nFundraisingList('NewFundraising')}
            </Button>
          </Stack>
        </CardHeader>
      )}
      <CardBody>
        {hasFundraising ? (
          <Tabs isLazy defaultIndex={0}>
            <TabList>
              <Tab>
                <Text>{i18nFundraisingList('InProgress')}</Text>
                <Badge ml="2">{started}</Badge>
              </Tab>
              <Tab>
                <Text>{i18nFundraisingList('Past')}</Text>
                <Badge ml="2">{passed}</Badge>
              </Tab>
              <Tab>
                <Text>{i18nFundraisingList('Draft')}</Text>
                <Badge ml="2">{drafts}</Badge>
              </Tab>
            </TabList>
            <TabPanels>
              <TabPanel>
                <FundraisingTableCurrent onClick={handleFundraisingClick} />
              </TabPanel>
              <TabPanel>
                <FundraisingTablePassed onClick={handleFundraisingClick} />
              </TabPanel>
              <TabPanel>
                <FundraisingTableDrafts onClick={handleFundraisingDraftClick} />
              </TabPanel>
            </TabPanels>
          </Tabs>
        ) : (
          <Stack layerStyle="emptyState">
            <Icon as={MailIcon} boxSize="40px" color="gray.500" />
            <Text>{i18nFundraisingTableEmpty('NoFundraising')}</Text>
          </Stack>
        )}
      </CardBody>
    </Card>
  );
}

export type FundraisingListProps = Props;
